import { Navigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../config/rolePermissions';

/**
 * The first department-aware gate in this application.
 *
 * Every other gate asks what ROLE you hold (`RoleBasedRoute`) or what TOOL you
 * may open (`ToolRoute`). This one asks who you are. `config/roleScopes.js`
 * says in its own header that the model is "role × department" — this is the
 * second half of that, finally enforced.
 *
 * ── Why the refusal is specific ─────────────────────────────────────────────
 *
 * A generic "Access denied" on a page whose entire purpose is "everything
 * relevant to you" is the least useful answer available. Somebody who lands on
 * the Design workspace from a shared link needs to be told which team they are
 * in and offered the door to their own, not scolded.
 *
 * ── This is cosmetic ────────────────────────────────────────────────────────
 *
 * `lib/workspace/authorize.js#assertCanRead` enforces the same rule on every
 * `/api/workspace/*` call. This exists so the answer arrives without a round
 * trip, not because it is the control.
 */
export default function DepartmentRoute({ children }) {
  const { user } = useAuth();
  const { key } = useParams();

  // Admins browse every department — the index page is theirs.
  if (user?.role === ROLES.ADMIN) return children;

  /**
   * EVERY department this person is in, not just their home one.
   *
   * The backend moved to the department SET in Phase 2 — home plus every team's
   * — so a Web Development lead who runs a Content team is allowed the Content
   * workspace by the API. This still compared the single `departmentKey` and
   * refused them in the UI, which is the worse half of a disagreement: the
   * person is told they may not, by a screen, about something they may.
   */
  const mineKeys = user?.departmentKeys?.length
    ? user.departmentKeys
    : [user?.departmentKey].filter(Boolean);
  const mine = mineKeys[0] || null;

  if (!mine) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-2xl border border-surface-200 bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-dark-900">You are not in a department yet</h2>
        <p className="mt-2 text-sm text-dark-700">
          A workspace shows one team&apos;s work, so it needs to know which team you are on.
          Ask an admin to set your department in Settings → People.
        </p>
      </div>
    );
  }

  if (!mineKeys.includes(key)) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-2xl border border-surface-200 bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-dark-900">That is another team&apos;s workspace</h2>
        <p className="mt-2 text-sm text-dark-700">
          {mineKeys.length > 1
            ? 'You have a workspace for each department you are in.'
            : 'You have the workspace for your own department.'}
        </p>
        <Link
          to={`/workspace/${mine}`}
          className="mt-4 inline-flex rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Open my workspace
        </Link>
      </div>
    );
  }

  return children;
}

/** `/workspace` with no key — send a member to theirs, an admin to the index. */
export function WorkspaceIndexRedirect({ children }) {
  const { user } = useAuth();
  if (user?.role === ROLES.ADMIN) return children;
  // First of their departments — a multi-department user lands somewhere real
  // rather than on an empty state.
  const mine = user?.departmentKeys?.[0] || user?.departmentKey;
  return mine ? <Navigate to={`/workspace/${mine}`} replace /> : children;
}
